import { useState, useEffect, useCallback, useMemo } from 'react';
import { transporterAPI } from '@/services/api';
import { useDebounce } from './useDebounce';

export const useTransporterOffers = (filters = {}) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
  });

  // Stabiliser les filtres pour éviter les boucles infinies
  const stableFilters = useMemo(() => filters, [
    filters.search,
    filters.status,
    filters.request_id,
  ]);

  // Debouncer les filtres pour éviter les requêtes trop fréquentes
  const debouncedFilters = useDebounce(stableFilters, 300);

  const fetchOffers = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      // Filtrer les paramètres vides ou "all"
      const cleanFilters = Object.fromEntries(
        Object.entries(debouncedFilters).filter(([key, value]) => 
          value !== '' && value !== 'all' && value != null
        )
      );
      
      const params = {
        page,
        ...cleanFilters,
      };
      
      // Utiliser l'endpoint pour récupérer les offres du transporteur
      const response = await transporterAPI.getOffers(params);
      setOffers(response.data.results || response.data || []);
      setPagination({
        count: response.data.count || 0,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (err) {
      console.error('Erreur lors du chargement des offres:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement des offres');
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);

  const getOfferById = useCallback(async (offerId) => {
    try {
      const response = await transporterAPI.getOfferById(offerId);
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors du chargement de l\'offre:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Erreur lors du chargement de l\'offre' 
      };
    }
  }, []);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  return {
    offers,
    loading,
    error,
    pagination,
    fetchOffers,
    getOfferById,
  };
};

